import React, { useState, useRef, useEffect } from "react"
import { useScale } from "helpers/ScaleContext"
import "./styles.css"

import {
  acceptanceHead,
  bargainingHead,
  denialHead,
  depressionHead,
  angerHead,
} from 'assets'

export default function StagesOfGrief() {
  const scale = useScale()

  const [currentStage, setCurrentStage] = useState("denial")
  const [isDragging, setIsDragging] = useState(false)
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 })
  const draggableRef = useRef(null)
  const containerRef = useRef(null)

  const stages = [
    { id: "denial", label: "I'm OK <br/> I'm Fine <br/> Gwenchana" },
    { id: "anger", label: "<span style='color: #FF3131'>WHY ME!?</span>" },
    { id: "bargaining", label: "<span style='font-style: italic'>thinking to turn back time</span>" },
    { id: "depression", label: "Leave me alone" },
    { id: "acceptance", label: "<b>Embrace & Elevate</b>" },
  ]

  // Handle mouse down / touch start
  const handleDragStart = (e) => {
    e.preventDefault()
    
    // Get cursor position
    const clientX = ("touches" in e ? e.touches[0].clientX : e.clientX) / scale
    const clientY = ("touches" in e ? e.touches[0].clientY : e.clientY) / scale
    
    // Set initial position
    setDragPosition({ x: clientX, y: clientY })
    setIsDragging(true)
  }

  // Handle mouse move / touch move
  const handleDrag = (e) => {
    if (!isDragging) return
    
    // Get current cursor position
    const clientX = ("touches" in e ? e.touches[0].clientX : e.clientX) / scale
    const clientY = ("touches" in e ? e.touches[0].clientY : e.clientY) / scale
    
    // Update position
    setDragPosition({ x: clientX, y: clientY })
  }

  // Handle mouse up / touch end
  const handleDragEnd = () => {
    if (!isDragging) return
    setIsDragging(false)

    // Find the closest stage
    if (draggableRef.current && containerRef.current) {
      const draggableRect = draggableRef.current.getBoundingClientRect()
      const draggableCenter = {
        x: draggableRect.left + draggableRect.width / 2,
        y: draggableRect.top + draggableRect.height / 2,
      }

      let closestStage = null
      let closestDistance = Number.POSITIVE_INFINITY

      const stageElements = containerRef.current.querySelectorAll(".stage")
      stageElements.forEach((stageEl) => {
        const stageRect = stageEl.getBoundingClientRect()
        const stageCenter = {
          x: stageRect.left + stageRect.width / 2,
          y: stageRect.top + stageRect.height / 2,
        }

        const distance = Math.sqrt(
          Math.pow(draggableCenter.x - stageCenter.x, 2) + Math.pow(draggableCenter.y - stageCenter.y, 2),
        )

        if (distance < closestDistance) {
          closestDistance = distance
          closestStage = stageEl.getAttribute("data-stage")
        }
      })

      if (closestStage) {
        setCurrentStage(closestStage)
      }
    }
  }

  // Set up event listeners
  useEffect(() => {
    const handleMouseMove = (e) => handleDrag(e)
    const handleTouchMove = (e) => handleDrag(e)
    const handleMouseUp = () => handleDragEnd()
    const handleTouchEnd = () => handleDragEnd()

    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove)
      window.addEventListener("touchmove", handleTouchMove, { passive: false })
      window.addEventListener("mouseup", handleMouseUp)
      window.addEventListener("touchend", handleTouchEnd)
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("touchmove", handleTouchMove)
      window.removeEventListener("mouseup", handleMouseUp)
      window.removeEventListener("touchend", handleTouchEnd)
    }
  }, [isDragging])

  // Prevent default touch behavior to avoid scrolling while dragging
  useEffect(() => {
    const preventDefaultTouch = (e) => {
      if (isDragging) {
        e.preventDefault()
      }
    }
    window.addEventListener("touchmove", preventDefaultTouch, { passive: false })
    return () => {
      window.removeEventListener("touchmove", preventDefaultTouch)
    }
  }, [isDragging])

  // Handle stage click directly (alternative to drag and drop)
  const handleStageClick = (stageId) => {
    if (!isDragging) {
      setCurrentStage(stageId)
    }
  }

  return (
    <div className="grief-container">
      {/* <h1 className="grief-title">The 5 Stages of Grief</h1> */}

      <div className="stages-wrapper" ref={containerRef}>
        <div className="stages-row">

          {stages.map((stage, index) => (
            <React.Fragment key={stage.id}>
              <div
                style={{ cursor: "pointer" }}
                className={`stage ${currentStage === stage.id ? "active" : ""}`}
                data-stage={stage.id}
                onClick={() => handleStageClick(stage.id)}
              >
                <div className="stage-id">{index + 1}. {stage.id}</div>

                {currentStage === stage.id && !isDragging && (
                  <div
                    ref={draggableRef}
                    className="draggable"
                    onMouseDown={handleDragStart}
                    onTouchStart={handleDragStart}
                  >
                    <img
                      src={currentStage === "denial" ? denialHead : currentStage === "anger" ? angerHead : currentStage === "bargaining" ? bargainingHead : currentStage === "depression" ? depressionHead : acceptanceHead}
                      alt={stage.id}
                    />
                    <div
                      className="stage-label"
                      dangerouslySetInnerHTML={{
                        __html: `${stage.label}`,
                      }}
                    />
                  </div>
                )}
              </div>

              {index < stages.length - 1 && (
                <div className="dotted-line"></div>
              )}
            </React.Fragment>
          ))}

          {isDragging && (
            <div
              ref={draggableRef}
              className="draggable dragging"
              style={{
                position: "fixed", // Use fixed positioning for more accurate tracking
                left: `${dragPosition.x}px`,
                top: `${dragPosition.y}px`,
                transform: "translate(-50%, -50%)", // Center the element on the cursor
                pointerEvents: "none", // Prevent the element from interfering with mouse events
              }}
            >
              <img
                src={currentStage === "denial" ? denialHead : currentStage === "anger" ? angerHead : currentStage === "bargaining" ? bargainingHead : currentStage === "depression" ? depressionHead : acceptanceHead}
                alt={currentStage || ""}
              />
            </div>
          )}
        </div>

        {/* <div className="instructions">
          <p>Drag the emotion through each stage of grief, from denial to acceptance.</p>
        </div> */}
      </div>
    </div>
  )
}

